import React, { useContext, useEffect, useState } from "react";
import { Box, Button, Container, Paper, Typography, Chip, useTheme, useMediaQuery, IconButton } from "@mui/material";
import { 
  ContactEmergency as ContactEmergencyIcon,
  Add as AddIcon,
  EventNote as EventNoteIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon
} from "@mui/icons-material";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timelinePlugin from "@fullcalendar/timeline";
import listPlugin from "@fullcalendar/list";
import AppointmentModal from "../components/appointments/AppointmentModal";
import ConfirmModal from "../components/appointments/ConfirmModal";
import { toast } from "react-toastify";
import TopNav from "../components/topnav";
import { secureApi, ADMIN, MEDICAL_ASSISTANT } from "../config";
import dayjs from "dayjs";
import { Context } from "../context";
import { useNavigate } from "react-router-dom";

const now = new Date();
const year = now.getFullYear();
const month = now.getMonth() + 1;
const day = now.getDate();
const hour = now.getHours();
const minute = now.getMinutes();

const getStatusColor = (status) => {
  switch (status) {
    case "pending": return { bg: "#f5f5f5", text: "#757575", border: "#bdbdbd", gradientBg: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)" };
    case "verified": return { bg: "#e3f2fd", text: "#1976d2", border: "#42a5f5", gradientBg: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)" };
    case "confirmed": return { bg: "#e8f5e9", text: "#2e7d32", border: "#66bb6a", gradientBg: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)" };
    default: return { bg: "#ffebee", text: "#c62828", border: "#ef5350", gradientBg: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)" };
  }
};

const Appointments = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { setLoggedIn, user } = useContext(Context);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [appointment, setAppointment] = useState({});
  const [events, setEvents] = useState([]);
  const [state, setState] = useState("add");
  const [checkInfo, setCheckInfo] = useState();
  const [viewType, setViewType] = useState(isMobile ? "listDay" : "timeGridDay");

  useEffect(() => {
    // Adjust view based on screen size
    if (isMobile) {
      setViewType(viewType.includes("list") ? viewType : "listDay");
    }
  }, [isMobile]);

  useEffect(() => {
    const events = appointments.map((item, index) => {
      const start = new Date(
        `${item.date.$y}-${item.date.$M + 1}-${item.date.$D} ${item.time.$H}:${
          item.time.$m
        }`
      );
      const end = new Date(start.getTime() + parseInt(item.duration) * 60000);

      const colorScheme = getStatusColor(item.status);

      return {
        id: index,
        title: `${item.patient.patientInfo.first_name} ${item.patient.patientInfo.last_name}$$Doctor:${item.doctor}$$Place:${item.place}$$Type:${item.type}$$Status:${item.status}`,
        start,
        end,
        backgroundColor: colorScheme.gradientBg,
        borderColor: colorScheme.border,
        textColor: colorScheme.text,
        borderWidth: '2px',
        item,
      };
    });
    setEvents(events);
  }, [appointments]);

  useEffect(() => {
    // Loading state
    const fetchAppointments = async () => {
      try {
        const response = await secureApi(window.localStorage.getItem("alex-med-token")).get(`/appointments`);
        let { appointments } = response.data;
        appointments = appointments.map((item) => {
          return {
            ...item,
            date: dayjs(item.date),
            time: dayjs(`${item.date} ${item.time}`),
          };
        });
        setAppointments(appointments);
      } catch (e) {
        console.log(e);
        if (e.response && e.response.status === 401) {
          toast.warn("Session has been expired. You need to login again!");
          setLoggedIn(false);
          navigate("/login");
        } else {
          toast.error(e.response?.data?.message || "Failed to load appointments");
        }
      }
    };
    
    fetchAppointments();
  }, []);

  const hasPermission = () => {
    return user && (user.role === ADMIN || user.role === MEDICAL_ASSISTANT);
  };

  const handleCancel = () => {
    setConfirmModalOpen(false);
    setAppointmentModalOpen(false);
    if (checkInfo) {
      checkInfo.revert();
      setCheckInfo(null);
    }
  };

  const handleAdd = (e, appointment) => {
    if (hasPermission()) {
      secureApi(window.localStorage.getItem("alex-med-token"))
        .post(`/appointment`, {
          patient_id: appointment.patient._id,
          doctor: appointment.doctor,
          place: appointment.place,
          type: appointment.type,
          date: appointment.date.format("MM/DD/YYYY"),
          time: appointment.time.format("hh:mm A"),
          duration: appointment.duration,
          status: appointment.status,
        })
        .then((response) => {
          const { appointment_id } = response.data;
          toast.success("New appointment has been created successfully!");
          setAppointments([
            ...appointments,
            {
              _id: appointment_id,
              patient_id: appointment.patient._id,
              patient: appointment.patient,
              doctor: appointment.doctor,
              place: appointment.place,
              type: appointment.type,
              date: appointment.date,
              time: appointment.time,
              duration: appointment.duration,
              status: appointment.status,
            },
          ]);
        })
        .catch((e) => {
          console.log(e);
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else {
            toast.error(e.response?.data?.message || "Failed to create appointment");
          }
        });
    } else toast.error("You don't have permission to create appointments");
    setAppointmentModalOpen(false);
  };

  const handleUpdate = (e, appointment, appointment_id) => {
    if (hasPermission()) {
      secureApi(window.localStorage.getItem("alex-med-token"))
        .put(`/appointment/${appointment_id}`, {
          patient_id: appointment.patient._id,
          doctor: appointment.doctor,
          place: appointment.place,
          type: appointment.type,
          date: appointment.date.format("MM/DD/YYYY"),
          time: appointment.time.format("hh:mm A"),
          duration: appointment.duration,
          status: appointment.status,
        })
        .then(() => {
          toast.success("Appointment has been updated successfully!");
          let newAppointments = [...appointments];
          for (let i = 0; i < newAppointments.length; i++) {
            if (newAppointments[i]._id === appointment_id) {
              newAppointments[i] = {
                _id: appointment_id,
                patient_id: appointment.patient._id,
                patient: appointment.patient,
                doctor: appointment.doctor,
                place: appointment.place,
                type: appointment.type,
                date: appointment.date,
                time: appointment.time,
                duration: appointment.duration,
                status: appointment.status,
              };
              break;
            }
          }
          setAppointments(newAppointments);
        })
        .catch((e) => {
          console.log(e);
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else {
            toast.error(e.response?.data?.message || "Failed to update appointment");
          }
        });
    } else toast.error("You don't have permission to update appointments.");
    setAppointmentModalOpen(false);
  };

  const handleDelete = (e, appointment_id) => {
    if (hasPermission()) {
      secureApi(window.localStorage.getItem("alex-med-token"))
        .delete(`/appointment/${appointment_id}`)
        .then(() => {
          let newAppointments = [...appointments].filter(
            (item) => item._id !== appointment_id
          );
          setAppointments(newAppointments);
          toast.success("Appointment has been removed successfully!");
        })
        .catch((e) => {
          console.log(e);
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else {
            toast.error(e.response?.data?.message || "Failed to delete appointment");
          }
        });
    } else toast.error("You don't have permission to delete appointments.");
    setAppointmentModalOpen(false);
  };

  const handleEventClick = (selectInfo) => {
    setState("update");
    setAppointment(appointments[parseInt(selectInfo.event.id)]);
    setAppointmentModalOpen(true);
  };

  const handleEventDrop = (checkInfo) => {
    if (hasPermission()) {
      setState("drop");
      setCheckInfo(checkInfo);
      setConfirmModalOpen(true);
    } else {
      toast.warn("You don't have permission to update appointments.");
      checkInfo.revert();
    }
  };

  const handleDrop = () => {
    if (!hasPermission()) {
      toast.warn("You don't have permission to update appointments.");
      return;
    }

    const { id, start } = checkInfo.event;
    const year = start.getFullYear();
    const month = start.getMonth() + 1;
    const date = start.getDate();
    const hours = start.getHours();
    const minutes = start.getMinutes();
    const appointment = appointments[parseInt(id)];

    const dateStr = `${("0" + month).substr(-2)}/${("0" + date).substr(
      -2
    )}/${year}`;
    const timeStr = `${("0" + hours).substr(-2)}:${("0" + minutes).substr(-2)}`;

    secureApi(window.localStorage.getItem("alex-med-token"))
      .put(`/appointment/${appointment._id}`, {
        patient_id: appointment.patient_id,
        doctor: appointment.doctor,
        place: appointment.place,
        type: appointment.type,
        duration: appointment.duration,
        status: appointment.status,
        date: dateStr,
        time: timeStr,
      })
      .then(() => {
        let newAppointments = [...appointments];
        for (let i = 0; i < appointments.length; i++) {
          if (newAppointments[i]._id === appointment._id) {
            newAppointments[i] = {
              ...newAppointments[i],
              date: dayjs(dateStr),
              time: dayjs(`${dateStr} ${timeStr}`),
            };
            break;
          }
        }
        setAppointments(newAppointments);
        toast.success("Appointment has been updated successfully!");
      })
      .catch((e) => {
        console.log(e);
        if (e.response && e.response.status === 401) {
          toast.warn("Session has been expired. You need to login again!");
          setLoggedIn(false);
          navigate("/login");
        } else {
          toast.error(e.response?.data?.message || "Failed to update appointment");
        }
      });

    setCheckInfo(null);
    setConfirmModalOpen(false);
  };

  const handleEventResize = (checkInfo) => {
    if (hasPermission()) {
      setState("resize");
      setCheckInfo(checkInfo);
      setConfirmModalOpen(true);
    } else {
      toast.warn("You don't have permission to update appointments.");
      checkInfo.revert();
    }
  };

  const handleResize = () => {
    if (!hasPermission()) {
      toast.warn("You don't have permission to update appointments.");
      return;
    }
    const { id, start, end } = checkInfo.event;
    const duration = (end.getTime() - start.getTime()) / 60000;

    if (
      duration !== 15 &&
      duration !== 30 &&
      duration !== 45 &&
      duration !== 60 &&
      duration !== 90
    ) {
      toast.warn(
        `Duration is ${duration}min. Duration should be 15min, 30min, 45min, 60min or 90min!`
      );
      checkInfo.revert();
    } else {
      const appointment = appointments[parseInt(id)];
      secureApi(window.localStorage.getItem("alex-med-token"))
        .put(`/appointment/${appointment._id}`, {
          patient_id: appointment.patient_id,
          doctor: appointment.doctor,
          place: appointment.place,
          type: appointment.type,
          duration: `${duration}min`,
          status: appointment.status,
          date: appointment.date.format("MM/DD/YYYY"),
          time: appointment.time.format("hh:mm A"),
        })
        .then(() => {
          let newAppointments = [...appointments];
          for (let i = 0; i < appointments.length; i++) {
            if (newAppointments[i]._id === appointment._id) {
              newAppointments[i] = {
                ...newAppointments[i],
                duration: `${duration}min`,
              };
              break;
            }
          }
          setAppointments(newAppointments);
          toast.success("Appointment has been updated successfully!");
        })
        .catch((e) => {
          console.log(e);
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else {
            toast.error(e.response?.data?.message || "Failed to update appointment");
          }
        });
    }
    setCheckInfo(null);
    setConfirmModalOpen(false);
  };

  // No separate legend needed - status will be displayed in each event

  const handleAddAppointment = () => {
    setState("add");
    setAppointment({
      patient: null,
      doctor: "",
      place: "",
      type: "Pain Management",
      duration: "15min",
      status: "pending",
      date: dayjs(`${month}/${day}/${year}`),
      time: dayjs(
        `${month}/${day}/${year} ${hour}:${minute}`
      ),
    });
    setAppointmentModalOpen(true);
  };

  return (
    <>
      <TopNav item="appointments" />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 2, 
            mb: 4, 
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0
          }}>

            <Typography variant="h5" component="h1" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
            <EventNoteIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Appointment Schedule
            </Typography>

            
            {hasPermission() && (
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<AddIcon />}
                onClick={handleAddAppointment}
                sx={{ 
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  px: 3
                }}
              >
                New Appointment
              </Button>
            )}
          </Box>
          

          
          <Box sx={{ 
            '& .fc': { 
              fontFamily: theme.typography.fontFamily,
              '& .fc-toolbar-title': {
                fontSize: isMobile ? '1.2rem' : '1.5rem',
                fontWeight: 'bold',
                color: theme.palette.text.primary
              },
              '& .fc-button': {
                textTransform: 'capitalize',
                borderRadius: '8px',
                boxShadow: 'none',
                transition: 'all 0.2s'
              },
              '& .fc-button-primary': {
                backgroundColor: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  borderColor: theme.palette.primary.dark
                }
              },
              '& .fc-button-active': {
                backgroundColor: `${theme.palette.primary.dark} !important`,
                borderColor: `${theme.palette.primary.dark} !important`,
                boxShadow: `0 0 0 0.2rem ${theme.palette.primary.light}80 !important`
              },
              '& .fc-event': {
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                overflow: 'hidden',
                border: 'none',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
                }
              },
              '& .fc-timegrid-event-harness-inset .fc-timegrid-event': {
                boxShadow: 'none'
              },
              '& .fc-list-event': {
                borderLeft: '4px solid',
                transition: 'background-color 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04) !important'
                }
              },
              '& .fc-day-today': {
                backgroundColor: `${theme.palette.primary.light}15 !important`
              },
              '& th': {
                padding: '12px 0',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                letterSpacing: '0.5px'
              }
            }
          }}>
            <FullCalendar
              plugins={[
                dayGridPlugin,
                timeGridPlugin,
                interactionPlugin,
                listPlugin,
                timelinePlugin,
              ]}
              initialView={viewType}
              headerToolbar={{
                left: isMobile ? 'prev,next' : 'prev,next today',
                center: 'title',
                right: isMobile 
                  ? 'listDay,listWeek,timeGridDay' 
                  : 'timeGridDay,timeGridWeek,dayGridMonth,listWeek'
              }}
              views={{
                listDay: { buttonText: 'Day List' },
                listWeek: { buttonText: 'Week List' },
                listMonth: { buttonText: 'Month List' },
                timeGridDay: { buttonText: 'Day' },
                timeGridWeek: { buttonText: 'Week' },
                dayGridMonth: { buttonText: 'Month' }
              }}
              slotDuration="00:15:00"
              slotLabelInterval="00:15:00"
              slotLabelFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              }}
              weekends={true}
              events={events}
              editable={hasPermission()}
              selectable={hasPermission()}
              dayMaxEvents={true}
              eventClick={handleEventClick}
              eventContent={renderEventContent}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              height={isMobile ? 'auto' : 800}
              allDaySlot={false}
              nowIndicator={true}
              businessHours={{
                daysOfWeek: [1, 2, 3, 4, 5],
                startTime: '08:00',
                endTime: '18:00',
              }}
            />
          </Box>
        </Paper>

        <AppointmentModal
          openModal={appointmentModalOpen}
          handleCancel={handleCancel}
          handleDelete={handleDelete}
          handleAdd={handleAdd}
          handleUpdate={handleUpdate}
          state={state}
          appointment={appointment}
          setAppointment={setAppointment}
        />
        <ConfirmModal
          openModal={confirmModalOpen}
          state={state}
          handleCancel={handleCancel}
          handleDrop={handleDrop}
          handleResize={handleResize}
        />
      </Container>
    </>
  );
};

  // a custom render function with enhanced styling
function renderEventContent(eventInfo) {
  const eventData = eventInfo.event.title.split("$$");
  const patient = eventData[0];
  const doctor = eventData[1].split(":")[1];
  const place = eventData[2].split(":")[1];
  const type = eventData[3].split(":")[1];
  const status = eventData[4].split(":")[1];

  const isMobile = window.innerWidth < 960;
  
  // Get status color for text
  const statusColor = getStatusColor(status).text;

  // Short display for mobile or when space is limited
  if (isMobile || eventInfo.view.type.includes("month") || eventInfo.timeText === "") {
    return (
      <Box sx={{ 
        p: 1, 
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        borderLeft: `4px solid ${getStatusColor(status).border}`,
      }}>
        <Typography variant="subtitle2" component="div" noWrap sx={{ fontWeight: 'bold' }}>
          {patient}
        </Typography>
        {eventInfo.timeText && (
          <Typography variant="caption" component="div" noWrap>
            {eventInfo.timeText}
          </Typography>
        )}
      </Box>
    );
  }

  // Full display for larger screens and time-based views
  return (
    <Box sx={{ 
      p: 1.5, 
      height: '100%',
      position: 'relative',
      boxShadow: `inset 0 0 0 2px ${getStatusColor(status).border}`,
      borderRadius: '4px',
      background: getStatusColor(status).gradientBg,
    }}>
      <Typography variant="subtitle2" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        {eventInfo.timeText}
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <ContactEmergencyIcon fontSize="small" sx={{ mr: 0.5 }} />
        <Typography variant="body2" component="div" noWrap fontWeight="medium">
          {patient}
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        <Box sx={{ 
          display: 'inline-flex', 
          alignItems: 'center',
          bgcolor: 'rgba(0,0,0,0.04)', 
          px: 1, 
          py: 0.25, 
          borderRadius: '12px',
          fontSize: '0.7rem'
        }}>
          <TimeIcon sx={{ fontSize: '0.875rem', mr: 0.5 }} />
          {type}
        </Box>
        
        <Box sx={{ 
          display: 'inline-flex', 
          alignItems: 'center',
          bgcolor: 'rgba(0,0,0,0.04)', 
          px: 1, 
          py: 0.25, 
          borderRadius: '12px',
          fontSize: '0.7rem'
        }}>
          Dr. {doctor}
        </Box>
        
        {place && (
          <Box sx={{ 
            display: 'inline-flex', 
            alignItems: 'center',
            bgcolor: 'rgba(0,0,0,0.04)', 
            px: 1, 
            py: 0.25, 
            borderRadius: '12px',
            fontSize: '0.7rem'
          }}>
            <LocationIcon sx={{ fontSize: '0.875rem', mr: 0.5 }} />
            {place}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Appointments;